import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@material-ui/core/styles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";
import { Button, Grid, IconButton, StepContent, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Box } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import LinkIcon from "@material-ui/icons/Link";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import {
  listQueueOptions,
  createQueueOption,
  updateQueueOption,
  deleteQueueOption,
} from "../../services/queueOptions.api";
import { findAllQueues } from "../../services/queue.api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AttachFile, DeleteOutline } from "@material-ui/icons";
import { head } from "lodash";
import { i18n } from "../../translate/i18n";
import TransferQueueModal from "../TransferQueueModal";
import { useStyles } from "./QueueOptionsStyles";

export function QueueOptionStepper({ queueId, options, updateOptions }) {
  const classes = useStyles();
  const [activeOption, setActiveOption] = useState(-1);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedOptionForTransfer, setSelectedOptionForTransfer] = useState(null);
  const [deleteTransferModalOpen, setDeleteTransferModalOpen] = useState(false);
  const [optionToDeleteTransfer, setOptionToDeleteTransfer] = useState(null);
  const [queues, setQueues] = useState([]);


  // ✅ CARGAR DEPARTAMENTOS PARA TRANSFERENCIA
  const loadQueues = async () => {
    try {
      const { data } = await findAllQueues();
      setQueues(data);
    } catch (e) {
      toastError(e);
    }
  };

  // ✅ ABRIR MODAL DE TRANSFERENCIA
  const openTransferModal = (option) => {
    setSelectedOptionForTransfer(option);
    setTransferModalOpen(true);
    loadQueues();
  };

  // ✅ ELIMINAR TRANSFERENCIA
  const handleRemoveTransfer = async (option) => {
    try {
      // ✅ ABRIR MODAL DE CONFIRMACIÓN
      setOptionToDeleteTransfer(option);
      setDeleteTransferModalOpen(true);
    } catch (e) {
      toastError(e);
    }
  };

  const confirmDeleteTransfer = async () => {
    try {
      const option = optionToDeleteTransfer;
      
      const optionToUpdate = { 
        ...option, 
        transferQueueId: null,
        transferQueue: null // ✅ TAMBIÉN LIMPIAR EL OBJETO transferQueue
      };
      
      // ✅ GUARDAR TEMPORALMENTE EN MEMORIA
      const optionIndex = options.findIndex(opt => opt === option);
      if (optionIndex !== -1) {
        options[optionIndex] = optionToUpdate;
        updateOptions(); // ✅ ACTUALIZAR EL ESTADO
      }
      
      toast.success("✅ Transferencia eliminada correctamente");
      
      // ✅ CERRAR MODAL
      setDeleteTransferModalOpen(false);
      setOptionToDeleteTransfer(null);
    } catch (e) {
      toastError(e);
    }
  };

  const cancelDeleteTransfer = () => {
    setDeleteTransferModalOpen(false);
    setOptionToDeleteTransfer(null);
  };

  // ✅ GUARDAR TRANSFERENCIA INMEDIATAMENTE EN BASE DE DATOS
  const handleSaveTransfer = async (transferQueueId) => {
    try {
      // ✅ Verificar que tengamos queueId válido
      if (!queueId) {
        toast.error("❌ Error: No se puede guardar la transferencia sin un departamento configurado");
        return;
      }

      // ✅ Buscar el departamento seleccionado para obtener sus datos completos
      const selectedQueue = queues.find(q => q.id === transferQueueId);
      
      // ✅ Preparar datos para actualizar la opción
      const optionToUpdate = { 
        ...selectedOptionForTransfer, 
        transferQueueId: transferQueueId || null,
        queueId: queueId // ✅ Asegurar que tenga queueId
      };

      // ✅ CRÍTICO: Guardar INMEDIATAMENTE en la base de datos
      const dataToSave = {
        ...optionToUpdate,
        transferQueueId: transferQueueId || null
      };
      
      if (selectedOptionForTransfer.id) {
        // ✅ OPCIÓN EXISTENTE: Actualizar en BD
        await updateQueueOption(selectedOptionForTransfer.id, dataToSave);
      } else {
        // ✅ NUEVA OPCIÓN: Crear en BD
        const { data } = await createQueueOption(dataToSave);
        optionToUpdate.id = data.id;
      }
      
      // ✅ ACTUALIZAR ESTADO EN MEMORIA
      const optionIndex = options.findIndex(opt => opt === selectedOptionForTransfer);
      if (optionIndex !== -1) {
        options[optionIndex] = {
          ...optionToUpdate,
          transferQueue: selectedQueue || null // ✅ ACTUALIZAR TAMBIÉN EL OBJETO transferQueue
        };
        updateOptions(); // ✅ ACTUALIZAR EL ESTADO
      }
      
      // ✅ MOSTRAR MENSAJE DE CONFIRMACIÓN
      if (transferQueueId) {
        toast.success(`✅ Transferencia guardada: → ${selectedQueue?.name || 'Departamento'}`);
      } else {
        toast.success("✅ Transferencia eliminada correctamente");
      }
      
      setTransferModalOpen(false);
      setSelectedOptionForTransfer(null);
    } catch (e) {
      console.error("❌ Error guardando transferencia:", e);
      toastError(e);
    }
  };

  const handleOption = (index) => async () => {
    setActiveOption(index);
    const option = options[index];

    if (option !== undefined && option.id !== undefined && option.children.length === 0) {
      try {
        await loadChildren(option);
        updateOptions();
      } catch (e) {
        toastError(e);
      }
    }
  };

  // ✅ FUNCIÓN PARA CARGAR HIJOS RECURSIVAMENTE
  const loadChildren = async (opt) => {
    if (!opt.id) return;

        const { data } = await listQueueOptions({ queueId, parentId: opt.id });
    opt.children = data.map((o) => ({
      ...o,
            children: [],
            edition: false,
    }));
    cleanCorruptedData(opt);

    await Promise.all(
      opt.children.map(async (child) => {
        await loadChildren(child);
      })
    );
  };

  // ✅ GUARDAR OPCIÓN EN ESTADO TEMPORAL (NO EN DB)
  const handleSave = async (option) => {
    try {
      // ✅ Verificar que tengamos queueId válido
      if (!queueId) {
        option.edition = false;
        updateOptions();
        toast.info("ℹ️ Opción guardada temporalmente. Guarde el departamento primero.");
        return;
      }
      
      // ✅ Asegurar que tenga queueId
      if (!option.queueId) {
        option.queueId = queueId;
      }
      
      // ✅ Asegurar que transferQueueId se envíe correctamente
      const dataToSend = {
        ...option,
        transferQueueId: option.transferQueueId || null
      };
      
      if (option.id) {
        await updateQueueOption(option.id, dataToSend);
      } else {
        const { data } = await createQueueOption(dataToSend);
        option.id = data.id;
      }
      
      option.edition = false;
      updateOptions();
      toast.success("✅ Opción guardada correctamente.");
    } catch (e) {
      toastError(e);
    }
  };



  const handleEdition = (index) => {
    options[index].edition = !options[index].edition;
    updateOptions();
  };

  // ✅ FUNCIÓN ESPECÍFICA PARA EDITAR SUB-OPCIONES
  const handleSubOptionEdition = (parentOption, childIndex) => {
    console.log("🔍 INICIANDO EDICIÓN DE SUB-OPCIÓN:", {
      parentTitle: parentOption.title,
      childIndex,
      totalChildren: parentOption.children?.length || 0
    });
    
    // ✅ VERIFICAR QUE LA SUB-OPCIÓN EXISTA
    if (!parentOption.children || !parentOption.children[childIndex]) {
      console.error("❌ SUB-OPCIÓN NO ENCONTRADA:", { childIndex, children: parentOption.children });
      return;
    }
    
    // ✅ TOGGLE MODO EDICIÓN DE LA SUB-OPCIÓN
    const childOption = parentOption.children[childIndex];
    childOption.edition = !childOption.edition;
    
    console.log("🔍 ESTADO DE EDICIÓN CAMBIADO:", {
      parentTitle: parentOption.title,
      childTitle: childOption.title,
      childIndex,
      isEditing: childOption.edition,
      childOption: childOption
    });
    
    updateOptions();
  };

  // ✅ FUNCIÓN PARA CAMBIAR TÍTULO DE SUB-OPCIÓN
  const handleSubOptionTitleChange = (parentOption, childIndex, event) => {
    parentOption.children[childIndex].title = event.target.value;
    updateOptions();
  };

  // ✅ FUNCIÓN PARA CAMBIAR MENSAJE DE SUB-OPCIÓN
  const handleSubOptionMessageChange = (parentOption, childIndex, event) => {
    parentOption.children[childIndex].message = event.target.value;
    updateOptions();
  };

  // ✅ FUNCIÓN PARA LIMPIAR DATOS CORRUPTOS
  const cleanCorruptedData = (option) => {
    if (!option.children || !option.id) return;
    
    // ✅ CONVERTIR option.id A NÚMERO PARA COMPARACIÓN CORRECTA
    const optionId = Number(option.id);
    
    // ✅ FILTRAR SOLO SUB-OPCIONES VÁLIDAS
    const validChildren = option.children.filter(child => {
      // ✅ CONVERTIR child.parentId A NÚMERO Y COMPARAR
      const childParentId = child.parentId !== null && child.parentId !== undefined 
        ? Number(child.parentId) 
        : null;
      
      // ✅ VALIDAR QUE EL parentId COINCIDA CON EL ID DE LA OPCIÓN PADRE
      if (childParentId !== optionId) {
        return false;
      }
      
      // ✅ VERIFICAR QUE NO TENGA TÍTULO DUPLICADO DEL PADRE
      if (child.title === option.title) {
        return false;
      }
      
      return true;
    });
    
    // ✅ ACTUALIZAR ARRAY DE HIJOS
    if (validChildren.length !== option.children.length) {
      option.children = validChildren;
    }
  };

  // ✅ FUNCIÓN PARA AGREGAR SUB-SUB-OPCIONES (NIVEL 3)
  const handleAddSubSubOption = (parentOption, childIndex) => {
    const childOption = parentOption.children[childIndex];

    // ✅ VERIFICAR QUE LA SUB-OPCIÓN ESTÉ GUARDADA (TENGA ID)
    if (!childOption.id) {
      toastError("Guarde la sub-opción primero para agregar sub-sub-opciones.");
      return;
    }
    
    // ✅ VERIFICAR LÍMITE DE SUB-SUB-OPCIONES
    if (childOption.children && childOption.children.length >= 3) {
      toastError("Máximo 3 sub-sub-opciones por sub-opción");
      return;
    }
    
    // ✅ AGREGAR SUB-SUB-OPCIÓN
    const subSubOptionNumber = (childOption.children?.length || 0) + 1;
    const newSubSubOption = {
      title: `Sub-sub-opción ${subSubOptionNumber}`,
      message: "",
      edition: true, // ✅ INICIAR EN MODO EDICIÓN PARA REGISTRAR DATOS INMEDIATAMENTE
      option: subSubOptionNumber.toString(),
      queueId: parentOption.queueId,
      parentId: childOption.id,
      children: [],
      depth: 3, // ✅ PROFUNDIDAD 3
    };
    
    childOption.children = childOption.children || [];
    childOption.children.push(newSubSubOption);
    updateOptions();
    
    // ✅ SUB-SUB-OPCIÓN AGREGADA (log removido para usuario final)
  };

  // ✅ FUNCIÓN PARA GUARDAR SUB-OPCIÓN
  const handleSubOptionSave = async (parentOption, childIndex) => {
    try {
      const childOption = parentOption.children[childIndex];
      
      if (childOption.id) {
        // ✅ ACTUALIZAR SUB-OPCIÓN EXISTENTE
        await updateQueueOption(childOption.id, {
          title: childOption.title,
          message: childOption.message,
          depth: 2
        });
        console.log("✅ SUB-OPCIÓN ACTUALIZADA:", childOption.title);
      } else {
        // ✅ CREAR NUEVA SUB-OPCIÓN
        const { data } = await createQueueOption({
          ...childOption,
          queueId: parentOption.queueId,
          parentId: parentOption.id,
          depth: 2
        });
        childOption.id = data.id;
        console.log("✅ SUB-OPCIÓN CREADA:", childOption.title);
      }
      
      // ✅ SALIR DEL MODO EDICIÓN
      childOption.edition = false;
      updateOptions();
      
    } catch (error) {
      console.error("❌ ERROR GUARDANDO SUB-OPCIÓN:", error);
      toastError(error);
    }
  };

  // ✅ NUEVA FUNCIÓN PARA GUARDAR SUB-SUB-OPCIÓN (NIVEL 3)
  const handleSubSubOptionSave = async (parentOption, childIndex, subSubIndex) => {
    try {
      const childOption = parentOption.children[childIndex];
      const subSubOption = childOption.children[subSubIndex];
      
      if (subSubOption.id) {
        // ✅ ACTUALIZAR SUB-SUB-OPCIÓN EXISTENTE
        await updateQueueOption(subSubOption.id, {
          title: subSubOption.title,
          message: subSubOption.message,
          depth: 3
        });
        console.log("✅ SUB-SUB-OPCIÓN ACTUALIZADA:", subSubOption.title);
      } else {
        // ✅ CREAR NUEVA SUB-SUB-OPCIÓN
        const { data } = await createQueueOption({
          ...subSubOption,
          queueId: parentOption.queueId,
          parentId: childOption.id,
          depth: 3
        });
        subSubOption.id = data.id;
        console.log("✅ SUB-SUB-OPCIÓN CREADA:", subSubOption.title);
      }
      
      // ✅ SALIR DEL MODO EDICIÓN
      subSubOption.edition = false;
      updateOptions();
      
    } catch (error) {
      console.error("❌ ERROR GUARDANDO SUB-SUB-OPCIÓN:", error);
      toastError(error);
    }
  };

  // ✅ NUEVA FUNCIÓN PARA EDITAR SUB-SUB-OPCIONES (OPCIONAL, PERO PARA CONSISTENCIA)
  const handleSubSubOptionEdition = (parentOption, childIndex, subSubIndex, toEdition) => {
    const childOption = parentOption.children[childIndex];
    const subSubOption = childOption.children[subSubIndex];
    subSubOption.edition = toEdition;
    updateOptions();
  };

  const handleDeleteOption = async (index) => {
    const option = options[index];
    if (option !== undefined && option.id !== undefined) {
      try {
        await deleteQueueOption(option.id);
      } catch (e) {
        toastError(e);
      }
    }
    options.splice(index, 1);
    options.forEach(async (option, order) => {
      option.option = order + 1;
      await handleSave(option);
    });
    updateOptions();
    // ✅ Notificar cambio en opciones (el useEffect detectará el cambio)
  };

  // ✅ NUEVA FUNCIÓN PARA ELIMINAR SUB-OPCIÓN (NIVEL 2)
  const handleDeleteSubOption = async (parentOption, childIndex) => {
    const childOption = parentOption.children[childIndex];
    if (childOption.id) {
      try {
        await deleteQueueOption(childOption.id);
      } catch (e) {
        toastError(e);
      }
    }
    parentOption.children.splice(childIndex, 1);
    updateOptions();
  };

  // ✅ NUEVA FUNCIÓN PARA ELIMINAR SUB-SUB-OPCIÓN (NIVEL 3)
  const handleDeleteSubSubOption = async (parentOption, childIndex, subSubIndex) => {
    const childOption = parentOption.children[childIndex];
    const subSubOption = childOption.children[subSubIndex];
    if (subSubOption.id) {
      try {
        await deleteQueueOption(subSubOption.id);
      } catch (e) {
        toastError(e);
      }
    }
    childOption.children.splice(subSubIndex, 1);
    updateOptions();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleOptionChangeTitle = (event, index) => {
    options[index].title = event.target.value;
    updateOptions();
  };

  const handleOptionChangeMessage = (event, index) => {
    options[index].message = event.target.value;
    updateOptions();
  };

  const renderTitle = (index) => {
    const option = options[index];
    
    // ✅ CALCULAR PROFUNDIDAD PARA MOSTRAR EN LA INTERFAZ
    let depth = 1;
    if (option.parentId) {
      depth = 2;
      // ✅ VERIFICAR SI ES SUB-OPCIÓN DE UNA SUB-OPCIÓN
      const parentOption = options.find(opt => opt.id === option.parentId);
      if (parentOption && parentOption.parentId) {
        depth = 3;
      }
    }
    
    // ✅ COLORES POR PROFUNDIDAD
    const depthColors = {
      1: "#1976d2", // Azul para opciones principales
      2: "#388e3c", // Verde para sub-opciones
      3: "#f57c00"  // Naranja para sub-sub-opciones
    };
    
    if (option.edition) {
      return (
        <>
          <TextField
            value={option.title}
            onChange={(event) => handleOptionChangeTitle(event, index)}
            size="small"
            className={classes.input}
            placeholder={i18n.t("queueOptions.placeholder.title")}
          />
                    <div style={{ display: "none" }}>
            <input
              type="file"
              ref={attachmentFile}
              onChange={(e) => handleAttachmentFile(e)}
            />
          </div>
          {option.edition && (
            <>
              <IconButton
                color="primary"
                variant="outlined"
                size="small"
                className={classes.button}
                onClick={() => handleSave(option)}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                variant="outlined"
                color="secondary"
                size="small"
                className={classes.button}
                onClick={() => handleDeleteOption(index)}
              >
                <DeleteOutlineIcon />
              </IconButton>
              {!attachment && !option.mediaPath && (
                <IconButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  className={classes.button}
                    onClick={() => attachmentFile.current.click()}
                  >
                  <AttachFile/>
                </IconButton>
              )}
                             {(option.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFile />}>
                        {attachment != null
                          ? attachment.name
                          : option.mediaName}
                      </Button>
                      
                        <IconButton
                          color="secondary"
                        >
                          <DeleteOutline />
                        </IconButton>
                    </Grid>
                  )}
              
              {/* ✅ ICONO DE TRANSFERENCIA DENTRO DEL MODO EDICIÓN */}
              <IconButton
                variant="outlined"
                size="small"
                className={classes.button}
                onClick={() => openTransferModal(option)}
                style={{ 
                  color: option.transferQueueId ? 'green' : 'gray',
                  marginLeft: '5px'
                }}
                title="Configurar transferencia de departamento"
              >
                <LinkIcon />
              </IconButton>
              
              {/* ✅ INDICADOR DE TRANSFERENCIA CONFIGURADA */}
              {option.transferQueueId && (
                <>
                  {/* ✅ Usar directamente option.transferQueue del backend */}
                  {(() => {
                    return option.transferQueue ? (
                      <Typography
                        variant="body2"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          marginLeft: '8px',
                          color: 'green',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                        onClick={() => openTransferModal(option)}
                        title="Click para editar transferencia"
                      >
                        → {option.transferQueue.name}
                      </Typography>
                    ) : null;
                  })()}
                  
                  {/* ✅ BOTÓN EDITAR TRANSFERENCIA */}
                  <IconButton
                    size="small"
                    className={classes.button}
                    onClick={() => openTransferModal(option)}
                    style={{ 
                      color: 'blue',
                      marginLeft: '4px'
                    }}
                    title="Editar transferencia"
                  >
                    <EditIcon />
                  </IconButton>
                  
                  {/* ✅ BOTÓN ELIMINAR TRANSFERENCIA */}
                  <IconButton
                    size="small"
                    className={classes.button}
                    onClick={() => handleRemoveTransfer(option)}
                    style={{ 
                      color: 'red',
                      marginLeft: '2px'
                    }}
                    title="Eliminar transferencia"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
        </>
      );
    }
    return (
      <>
        <Typography variant="body1">
          <span style={{ color: depthColors[depth] }}>
          {option.title !== "" ? option.title : i18n.t("queueOptions.undefinedTitle")}
          </span>
          <span style={{ 
            fontSize: "0.8em", 
            color: "white",
            marginLeft: "8px",
            backgroundColor: depthColors[depth],
            padding: "2px 6px",
            borderRadius: "10px"
          }}>
            Nivel {depth}
          </span>
          <IconButton
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={() => handleEdition(index)}
          >
            <EditIcon />
          </IconButton>
          
          {/* ✅ SOLO MOSTRAR TEXTO DE TRANSFERENCIA EN MODO NO-EDICIÓN */}
          {option.transferQueueId && (
            <>
              {(() => {
                const transferQueue = queues.find(q => q.id === option.transferQueueId);
                return transferQueue ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: '8px',
                      color: 'green',
                      fontSize: '0.875rem'
                    }}
                  >
                    → {transferQueue.name}
                  </span>
                ) : null;
              })()}
            </>
          )}
        </Typography>
      </>
    );
  };

  const renderMessage = (index) => {
    const option = options[index];
    if (option.edition) {
      return (
        <>
          <TextField
            style={{ width: "100%" }}
            multiline
            value={option.message}
            onChange={(event) => handleOptionChangeMessage(event, index)}
            size="small"
            className={classes.input}
            placeholder={i18n.t("queueOptions.placeholder.message")}
          />
        </>
      );
    }
    return (
      <>
        <Typography onClick={() => handleEdition(index)}>
          {option.message}
        </Typography>
      </>
    );
  };

  const handleAddOption = (index) => {
    // ✅ VALIDAR PROFUNDIDAD MÁXIMA (MÁXIMO 3 NIVELES)
    const currentOption = options[index];
    
    // ✅ VERIFICAR QUE LA OPCIÓN ESTÉ GUARDADA (TENGA ID)
    if (!currentOption.id) {
      toastError("Guarde la opción primero para agregar sub-opciones.");
      return;
    }

    // ✅ CALCULAR PROFUNDIDAD ACTUAL
    let currentDepth = 1;
    if (currentOption.parentId) {
      currentDepth = 2;
      // ✅ VERIFICAR SI ES SUB-OPCIÓN DE UNA SUB-OPCIÓN
      const parentOption = options.find(opt => opt.id === currentOption.parentId);
      if (parentOption && parentOption.parentId) {
        currentDepth = 3;
      }
    }
    
    console.log("🔍 VALIDACIÓN DE PROFUNDIDAD:", {
      optionTitle: currentOption.title,
      currentDepth,
      maxDepth: 3
    });
    
    // ✅ VERIFICAR LÍMITE DE PROFUNDIDAD
    if (currentDepth >= 3) {
      toastError(`No se pueden crear más de 3 niveles de profundidad. Profundidad actual: ${currentDepth}`);
      return;
    }
    
    // ✅ VERIFICAR LÍMITE DE SUB-OPCIONES POR NIVEL
    const maxSubOptions = currentDepth === 1 ? 10 : (currentDepth === 2 ? 5 : 3);
    
    // ✅ CONTAR SOLO SUB-OPCIONES VÁLIDAS (CON parentId CORRECTO)
    const validSubOptions = currentOption.children?.filter(child => child.parentId === currentOption.id) || [];
    
    if (validSubOptions.length >= maxSubOptions) {
      toastError(`Máximo ${maxSubOptions} sub-opciones por nivel. Profundidad actual: ${currentDepth}`);
      return;
    }
    
    // ✅ GENERAR NOMBRE ÚNICO PARA LA SUB-OPCIÓN
    const optionNumber = validSubOptions.length + 1;
    let newTitle = "";
    
    if (currentDepth === 1) {
      newTitle = `Sub-opción ${optionNumber}`;
    } else if (currentDepth === 2) {
      newTitle = `Sub-sub-opción ${optionNumber}`;
    }
    
    // ✅ LIMPIAR DATOS CORRUPTOS ANTES DE AGREGAR
    cleanCorruptedData(currentOption);
    
    // ✅ AGREGAR SUB-OPCIÓN CON PROFUNDIDAD VALIDADA
    const newChildOption = {
      title: newTitle,
      message: "",
      edition: true, // ✅ INICIAR EN MODO EDICIÓN PARA REGISTRAR DATOS INMEDIATAMENTE
      option: optionNumber.toString(),
      queueId,
      parentId: currentOption.id,
      children: [], // ✅ SIN HIJOS RECURSIVOS
      depth: currentDepth + 1, // ✅ AGREGAR PROFUNDIDAD
    };
    
    console.log("🔍 CREANDO SUB-OPCIÓN:", {
      title: newTitle,
      depth: newChildOption.depth,
      parentId: currentOption.id,
      edition: newChildOption.edition
    });
    
    currentOption.children = currentOption.children || [];
    currentOption.children.push(newChildOption);
    
    console.log("✅ SUB-OPCIÓN AGREGADA CON PROFUNDIDAD:", {
      title: newTitle,
      depth: newChildOption.depth,
      parentTitle: currentOption.title
    });
    
    // ✅ VERIFICAR ESTADO FINAL DE LOS DATOS
    const finalValidChildren = currentOption.children.filter(child => child.parentId === currentOption.id);
    console.log("🔍 ESTADO FINAL DE DATOS:", {
      totalChildren: currentOption.children.length,
      validChildren: finalValidChildren.length,
      validTitles: finalValidChildren.map(child => child.title)
    });
    
    updateOptions();
  };

  const renderStep = (option, index) => {
    return (
      <Step key={index}>
        <StepLabel style={{ cursor: "pointer" }} onClick={handleOption(index)}>
          {renderTitle(index)}
        </StepLabel>
        <StepContent>
          {renderMessage(index)}

          {option.id !== undefined && (
            <>
              {/* ✅ VALIDAR SI SE PUEDE AGREGAR MÁS SUB-OPCIONES */}
              {(() => {
                // ✅ CALCULAR PROFUNDIDAD ACTUAL
                let currentDepth = 1;
                if (option.parentId) {
                  currentDepth = 2;
                  const parentOption = options.find(opt => opt.id === option.parentId);
                  if (parentOption && parentOption.parentId) {
                    currentDepth = 3;
                  }
                }
                
                // ✅ VERIFICAR SI SE PUEDE AGREGAR MÁS
                const canAddMore = currentDepth < 3;
                const maxSubOptions = currentDepth === 1 ? 10 : (currentDepth === 2 ? 5 : 3);
                const hasReachedLimit = option.children && option.children.length >= maxSubOptions;
                
                return (
              <Button
                    color={canAddMore && !hasReachedLimit ? "primary" : "default"}
                size="small"
                onClick={() => handleAddOption(index)}
                startIcon={<AddIcon />}
                variant="outlined"
                className={classes.addButton}
                    disabled={!canAddMore || hasReachedLimit}
                    title={
                      !canAddMore 
                        ? "Máximo 3 niveles de profundidad alcanzado" 
                        : hasReachedLimit 
                          ? `Máximo ${maxSubOptions} sub-opciones por nivel alcanzado`
                          : `Agregar sub-opción (Nivel ${currentDepth + 1})`
                    }
                  >
                    {canAddMore && !hasReachedLimit 
                      ? i18n.t("queueOptions.add") 
                      : "Límite alcanzado"
                    }
              </Button>
                );
              })()}
            </>
          )}
          {/* ✅ SUB-OPCIONES HABILITADAS - MÁXIMO 3 NIVELES */}
          {option.children && option.children.length > 0 && option.parentId === null && (
            <div style={{ 
              marginLeft: "20px", 
              borderLeft: "2px solid #ddd", 
              paddingLeft: "15px",
              marginTop: "10px"
            }}>
              {/* ✅ CALCULAR SUB-OPCIONES REALES */}
              {(() => {
                const realSubOptions = option.children.filter(childOption => childOption.parentId === option.id);
                return (
                  <>
                    <Typography variant="body2" style={{ 
                      color: "#666", 
                      marginBottom: "10px",
                      fontWeight: "bold"
                    }}>
                      <span role="img" aria-label="lista de sub-opciones">📋</span> Sub-opciones ({realSubOptions.length})
                    </Typography>
                    
                    {/* ✅ RENDERIZAR SUB-OPCIONES SIN RECURSIVIDAD INFINITA */}
                    {realSubOptions.map((childOption, childIndex) => (
                <div key={childIndex} style={{
                  backgroundColor: "#f9f9f9",
                  padding: "8px",
                  margin: "5px 0",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0"
                }}>
                  {/* ✅ MODO EDICIÓN PARA SUB-OPCIONES */}
                  {childOption.edition ? (
                    <div>
                      <TextField
                        value={childOption.title}
                        onChange={(event) => handleSubOptionTitleChange(option, childIndex, event)}
                        size="small"
                        className={classes.input}
                        placeholder="Título de la sub-opción"
                        style={{ marginBottom: "8px" }}
                      />
                      <TextField
                        value={childOption.message}
                        onChange={(event) => handleSubOptionMessageChange(option, childIndex, event)}
                        size="small"
                        className={classes.input}
                        placeholder="Mensaje de la sub-opción"
                        multiline
                        rows={2}
                        style={{ marginBottom: "8px" }}
                      />
                      
                      {/* ✅ BOTONES DE ACCIÓN EN MODO EDICIÓN */}
                      <div style={{ display: "flex", gap: "5px" }}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleSubOptionSave(option, childIndex)}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleSubOptionEdition(option, childIndex)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() => handleDeleteSubOption(option, childIndex)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    /* ✅ MODO VISUALIZACIÓN PARA SUB-OPCIONES */
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <Typography variant="body2" style={{ fontWeight: "bold" }}>
                            {childOption.title || `Sub-opción ${childIndex + 1}`}
                          </Typography>
                          {childOption.message && (
                            <Typography variant="caption" style={{ color: "#666" }}>
                              {childOption.message}
                            </Typography>
                          )}
                        </div>
                        
                        {/* ✅ BOTÓN EDITAR PARA SUB-OPCIÓN */}
                        <IconButton
                          size="small"
                          onClick={() => handleSubOptionEdition(option, childIndex)}
                          style={{ marginLeft: "10px" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {/* ✅ BOTÓN ELIMINAR PARA SUB-OPCIÓN */}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSubOption(option, childIndex)}
                          style={{ marginLeft: "5px" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                      
                      {/* ✅ MOSTRAR PROFUNDIDAD */}
                      <div style={{
                        backgroundColor: "#388e3c",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "10px",
                        fontSize: "0.7em",
                        display: "inline-block",
                        marginTop: "5px"
                      }}>
                        Nivel 2
                      </div>
                      
                      {/* ✅ BOTÓN PARA AGREGAR SUB-SUB-OPCIONES (NIVEL 3) */}
                      {childOption.children && childOption.children.length < 3 && (
                        <Button
                          color="primary"
                          size="small"
                          onClick={() => handleAddSubSubOption(option, childIndex)}
                          startIcon={<AddIcon />}
                          variant="outlined"
                          style={{ 
                            marginTop: "8px", 
                            fontSize: "0.7em",
                            backgroundColor: "#f57c00",
                            color: "white",
                            borderColor: "#f57c00"
                          }}
                          title="Agregar sub-sub-opción (Nivel 3)"
                        >
                          + Agregar Nivel 3
                        </Button>
                      )}
                      
                      {/* ✅ MOSTRAR SUB-SUB-OPCIONES EXISTENTES */}
                      {childOption.children && childOption.children.length > 0 && (
                        <div style={{ 
                          marginTop: "10px", 
                          marginLeft: "15px", 
                          borderLeft: "2px solid #f57c00", 
                          paddingLeft: "10px" 
                        }}>
                          <Typography variant="caption" style={{ color: "#f57c00", fontWeight: "bold" }}>
                            Sub-sub-opciones ({childOption.children.length})
                          </Typography>
                          {childOption.children.map((subSubOption, subSubIndex) => (
                            <div key={subSubIndex} style={{
                              backgroundColor: "#fff3e0",
                              padding: "6px",
                              margin: "4px 0",
                              borderRadius: "4px",
                              border: "1px solid #ffcc02"
                            }}>
                              {/* ✅ MODO EDICIÓN PARA SUB-SUB-OPCIONES */}
                              {subSubOption.edition ? (
                                <div>
                                  <TextField
                                    size="small"
                                    placeholder="Título de sub-sub-opción"
                                    value={subSubOption.title}
                                    onChange={(e) => {
                                      subSubOption.title = e.target.value;
                                      updateOptions();
                                    }}
                                    style={{ marginBottom: "5px", width: "100%" }}
                                  />
                                  <TextField
                                    size="small"
                                    placeholder="Mensaje de respuesta"
                                    value={subSubOption.message}
                                    onChange={(e) => {
                                      subSubOption.message = e.target.value;
                                      updateOptions();
                                    }}
                                    style={{ marginBottom: "5px", width: "100%" }}
                                    multiline
                                    rows={2}
                                  />
                                  
                                  {/* ✅ BOTONES DE ACCIÓN EN MODO EDICIÓN */}
                                  <div style={{ display: "flex", gap: "3px" }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSubSubOptionSave(option, childIndex, subSubIndex)}
                                      style={{ backgroundColor: "#4caf50", color: "white" }}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSubSubOptionEdition(option, childIndex, subSubIndex, false)}
                                      style={{ backgroundColor: "#f44336", color: "white" }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSubSubOption(option, childIndex, subSubIndex)}
                                      style={{ backgroundColor: "#f44336", color: "white" }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                </div>
                              ) : (
                                /* ✅ MODO VISUALIZACIÓN PARA SUB-SUB-OPCIONES */
                                <div>
                                  <Typography variant="body2" style={{ fontSize: "0.8em" }}>
                                    {subSubOption.title || `Sub-sub-opción ${subSubIndex + 1}`}
                                  </Typography>
                                  
                                  {subSubOption.message && (
                                    <Typography variant="caption" style={{ color: "#666", fontSize: "0.7em" }}>
                                      {subSubOption.message}
                                    </Typography>
                                  )}
                                  
                                  <div style={{
                                    backgroundColor: "#f57c00",
                                    color: "white",
                                    padding: "1px 4px",
                                    borderRadius: "8px",
                                    fontSize: "0.6em",
                                    display: "inline-block",
                                    marginTop: "3px"
                                  }}>
                                    Nivel 3
                                  </div>
                                  
                                  {/* ✅ BOTÓN EDITAR SUB-SUB-OPCIÓN */}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSubSubOptionEdition(option, childIndex, subSubIndex, true)}
                                    style={{ 
                                      marginTop: "5px",
                                      backgroundColor: "#2196f3",
                                      color: "white"
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  {/* ✅ BOTÓN ELIMINAR SUB-SUB-OPCIÓN */}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteSubSubOption(option, childIndex, subSubIndex)}
                                    style={{ 
                                      marginTop: "5px",
                                      backgroundColor: "#f44336",
                                      color: "white"
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
                    </>
                  );
                })()}
            </div>
          )}
        </StepContent>
      </Step>
    );
  };

  const renderStepper = () => {
    return (
      <Stepper
        style={{ marginBottom: 0, paddingBottom: 0 }}
        nonLinear
        activeStep={activeOption}
        orientation="vertical"
      >
        {options.map((option, index) => renderStep(option, index))}
      </Stepper>
    );
  };

  return (
    <>
      {renderStepper()}
      
      {/* ✅ MODAL DE TRANSFERENCIA */}
      <TransferQueueModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        onSelect={(selectedQueue) => {
          handleSaveTransfer(selectedQueue.id);
        }}
        currentTransferQueueId={selectedOptionForTransfer?.transferQueueId}
        currentOptionId={selectedOptionForTransfer?.id}
        excludedQueueIds={
          // ✅ Filtrar departamentos ya usados en otras opciones del NIVEL 1
          options
            .filter(opt => 
              // Solo opciones del nivel 1 (sin parentId)
              !opt.parentId && 
              // Excluir la opción actual (para poder mantener su departamento)
              opt.id !== selectedOptionForTransfer?.id &&
              // Solo incluir si tiene transferQueueId asignado
              opt.transferQueueId
            )
            .map(opt => opt.transferQueueId)
            .filter(id => id !== null && id !== undefined)
        }
      />
      
      {/* ✅ MODAL DE CONFIRMACIÓN PARA ELIMINAR TRANSFERENCIA */}
      <Dialog
        open={deleteTransferModalOpen}
        onClose={cancelDeleteTransfer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Eliminar Transferencia
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            ¿Está seguro que desea eliminar la transferencia a → {optionToDeleteTransfer?.transferQueue?.name || 'el departamento'}?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta acción no se puede deshacer. La opción seguirá existiendo pero ya no tendrá departamento de transferencia asignado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteTransfer} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={confirmDeleteTransfer} 
            color="primary" 
            variant="contained"
            style={{ backgroundColor: '#f50057', color: 'white' }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
